import styled, { css } from 'styled-components';
import { SIDEBAR_MOBILE_BREAKPOINT } from '@constants/sidebar';
import {
  EDITOR_PANEL_HEADER_PAD,
  EDITOR_PANEL_TOGGLE_CENTER,
  EDITOR_TOPBAR_HEIGHT,
} from '../editorLayout.constants';

/* ── Dimensions ────────────────────────────────────────────────────────── */
export const DOCK_WIDTH_OPEN = 420;
export const DOCK_WIDTH_COLLAPSED = 60;
const EXPAND_BUTTON_WIDTH = 22;
/** The handle straddles the dock's left border, so half of it hangs outside. */
export const DOCK_EXPAND_OVERHANG = EXPAND_BUTTON_WIDTH / 2;
const TRANSITION = '280ms cubic-bezier(0.4, 0, 0.2, 1)';

// `border-left` is the only separator between the dock and the canvas
// (both share `surface`). The AI-agent panel divider is a separate
// inner element's `border-right`, not this one.
export const DockRoot = styled.div<{ $collapsed: boolean }>`
  position: fixed;
  top: ${EDITOR_TOPBAR_HEIGHT}px;
  right: 0;
  bottom: 0;
  width: ${({ $collapsed }) =>
    $collapsed ? DOCK_WIDTH_COLLAPSED : DOCK_WIDTH_OPEN}px;
  z-index: 12;
  background: ${({ theme }) => theme.colors?.surface ?? '#0E0F18'};
  border-left: 1px solid
    ${({ theme }) => theme.colors?.outlineVariant ?? '#25263A'};
  transition: width ${TRANSITION};
  pointer-events: auto;

  @media (max-width: ${SIDEBAR_MOBILE_BREAKPOINT}px) {
    ${({ $collapsed }) =>
      !$collapsed &&
      css`
        width: 100%;
      `}
  }
`;

/* ── Collapsed rail ───────────────────────────────────────────────────── */
export const CollapsedRail = styled.aside<{ $visible: boolean }>`
  position: absolute;
  inset: 0;
  width: ${DOCK_WIDTH_COLLAPSED}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: ${({ theme }) =>
    theme.colors?.surfaceContainer ?? '#181927'};
  padding: 12px 10px;
  gap: 8px;
  overflow: visible;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition: opacity
    ${({ $visible }) => ($visible ? '200ms ease 80ms' : '140ms ease')};
`;

export const ExpandButton = styled.button`
  position: absolute;
  top: ${EDITOR_PANEL_TOGGLE_CENTER}px;
  left: 0;
  transform: translate(-50%, -50%);
  width: ${EXPAND_BUTTON_WIDTH}px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors?.surface ?? '#0E0F18'};
  color: ${({ theme }) => theme.colors?.onSurfaceVariant ?? '#A1A3B8'};
  border: 1px solid
    ${({ theme }) => theme.colors?.outlineVariant ?? '#25263A'};
  border-radius: 999px;
  cursor: pointer;
  z-index: 100;
  padding: 0;
  transition: color 160ms, background 160ms,
    transform 200ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 160ms;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);

  &:hover {
    background: ${({ theme }) =>
      theme.colors?.surfaceContainerHigh ?? '#1F2030'};
    color: ${({ theme }) => theme.colors?.onSurface ?? '#E6E6F2'};
    transform: translate(-50%, -50%) scale(1.08);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

/* ── Expanded panel ───────────────────────────────────────────────────── */
// Carries {@code min-height: 0} so {@link PanelBody}'s {@code overflow-y: auto}
// actually kicks in. Without it the inner scroll container would grow with its
// content (classic Flexbox nested-scroll bug) and the execution log would push
// off the bottom of the viewport instead of scrolling inside the panel.
export const PanelAside = styled.aside<{ $visible: boolean }>`
  position: absolute;
  inset: 0;
  width: ${DOCK_WIDTH_OPEN}px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: ${({ theme }) => theme.colors?.surface ?? '#0E0F18'};
  overflow: visible;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition: opacity
    ${({ $visible }) => ($visible ? '200ms ease' : '140ms ease')};

  @media (max-width: ${SIDEBAR_MOBILE_BREAKPOINT}px) {
    width: 100%;
  }
`;

/* ── Header ───────────────────────────────────────────────────────────── */
export const PanelHeader = styled.header`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: ${EDITOR_PANEL_HEADER_PAD}px 12px;
  flex-shrink: 0;
`;

/* Breadcrumb area — matches design's .wf-panel__crumb */
export const BreadcrumbWrap = styled.div`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
  min-width: 0;
  overflow: visible;
`;

export const CrumbMuted = styled.span`
  color: ${({ theme }) => theme.colors?.onSurfaceVariant ?? '#A1A3B8'};
  white-space: nowrap;
  flex-shrink: 0;
`;

export const CrumbSep = styled.span`
  color: ${({ theme }) => theme.colors?.onSurfaceVariant ?? '#A1A3B8'};
  opacity: 0.6;
  flex-shrink: 0;
`;

export const CrumbAnchor = styled.div`
  position: relative;
  display: inline-flex;
  min-width: 0;
`;

export const CrumbCurrentButton = styled.button<{ $open?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  border: none;
  outline: none;
  background: transparent;
  color: ${({ theme }) => theme.colors?.onSurface ?? '#E6E6F2'};
  font-size: 13px;
  font-weight: 600;
  padding: 4px 6px;
  border-radius: 999px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: background 120ms ease;

  ${({ $open, theme }) =>
    $open &&
    css`
      background: ${theme.colors?.surfaceContainerHigh ?? '#1F2030'};
    `}

  &:hover {
    background: ${({ theme }) =>
      theme.colors?.surfaceContainerHigh ?? '#1F2030'};
  }

  svg {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
  }
`;

/* ── Body ─────────────────────────────────────────────────────────────── */
export const PanelBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 4px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  scrollbar-gutter: stable;

  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) =>
      theme.colors?.surfaceContainerHigh ?? '#1F2030'};
    border-radius: 100px;
    border: 2px solid transparent;
    background-clip: padding-box;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) =>
      theme.colors?.surfaceContainerHighest ?? '#262738'};
    background-clip: padding-box;
    border: 2px solid transparent;
  }
`;

/* ── Footer ───────────────────────────────────────────────────────────── */
export const PanelFooter = styled.div`
  position: relative;
  border-top: 1px solid
    ${({ theme }) => theme.colors?.outlineVariant ?? '#25263A'};
  padding: 15px 14px 19px;
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  flex-shrink: 0;
`;
